/**
 * Website Deployment
 * Admin controls scheduled + event-driven rebuilds of the public website
 * (auriksha.com) via its Render deploy hook. The site is a static export, so a
 * rebuild is what bakes the current gold rate into the crawlable HTML.
 *
 * - Deploy hook is stored ENCRYPTED on the server; only the last 4 chars come
 *   back for display. Leave the field blank to keep the existing hook.
 * - Schedule: daily, or weekly on chosen days, at a time in the chosen timezone.
 * - Auto-deploy after rate update: rebuild when the gold-rate date advances.
 * - Deploy Now: trigger an immediate rebuild.
 */

import React, { useEffect, useState } from 'react';
import {
    Card, Switch, Radio, Checkbox, Select, Input, Button, Space, Typography,
    Tag, Table, Spin, Alert, Popconfirm, Divider, message,
} from 'antd';
import {
    SaveOutlined, ReloadOutlined, RocketOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import {
    getWebsiteDeploymentConfig, updateWebsiteDeploymentConfig,
    deployWebsiteNow, getWebsiteDeploymentLogs,
} from '../api';

const { Title, Text, Paragraph } = Typography;

const DAY_OPTIONS = [
    { label: 'Sun', value: 0 }, { label: 'Mon', value: 1 }, { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 }, { label: 'Thu', value: 4 }, { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
];

const TZ_OPTIONS = [
    'Asia/Kolkata', 'UTC', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'America/New_York',
].map((z) => ({ label: z, value: z }));

const TRIGGER_COLOR = { scheduled: 'blue', manual: 'purple', 'rate-update': 'gold' };

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export default function WebsiteDeployment() {
    const [cfg, setCfg] = useState(null);
    const [hookInput, setHookInput] = useState('');
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deploying, setDeploying] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    const loadAll = async () => {
        setLoading(true);
        try {
            const [cfgRes, logRes] = await Promise.all([
                getWebsiteDeploymentConfig(),
                getWebsiteDeploymentLogs(20),
            ]);
            setCfg(cfgRes.data);
            setLogs(logRes.data || []);
        } catch (err) {
            messageApi.error(err.response?.data?.message || 'Failed to load deployment settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

    const patch = (changes) => setCfg((prev) => ({ ...prev, ...changes }));

    const handleSave = async () => {
        if (cfg.enabled && !cfg.deployHookConfigured && !hookInput.trim()) {
            return messageApi.error('Add the Render deploy hook before enabling the schedule');
        }
        if (cfg.frequency === 'weekly' && (!cfg.daysOfWeek || cfg.daysOfWeek.length === 0)) {
            return messageApi.error('Pick at least one day for a weekly schedule');
        }
        setSaving(true);
        try {
            const payload = {
                enabled: cfg.enabled,
                frequency: cfg.frequency,
                daysOfWeek: cfg.daysOfWeek || [],
                time: cfg.time,
                timezone: cfg.timezone,
                autoDeployAfterRateUpdate: cfg.autoDeployAfterRateUpdate,
            };
            if (hookInput.trim()) payload.deployHook = hookInput.trim();
            const res = await updateWebsiteDeploymentConfig(payload);
            setCfg(res.data);
            setHookInput('');
            messageApi.success('Deployment settings saved');
            loadAll();
        } catch (err) {
            messageApi.error(err.response?.data?.message || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleClearHook = async () => {
        setSaving(true);
        try {
            const res = await updateWebsiteDeploymentConfig({ deployHook: '' });
            setCfg(res.data);
            setHookInput('');
            messageApi.success('Deploy hook removed');
        } catch (err) {
            messageApi.error(err.response?.data?.message || 'Failed to remove hook');
        } finally {
            setSaving(false);
        }
    };

    const handleDeployNow = async () => {
        setDeploying(true);
        try {
            const res = await deployWebsiteNow();
            if (res.success) messageApi.success('Website deploy triggered on Render');
            else messageApi.error(res.data?.message || 'Deploy request was not accepted');
            loadAll();
        } catch (err) {
            messageApi.error(err.response?.data?.message || 'Failed to trigger deploy');
        } finally {
            setDeploying(false);
        }
    };

    if (loading || !cfg) {
        return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>;
    }

    const logColumns = [
        { title: 'When', dataIndex: 'triggeredAt', render: fmtDate, width: 180 },
        {
            title: 'Trigger', dataIndex: 'trigger', width: 130,
            render: (t) => <Tag color={TRIGGER_COLOR[t] || 'default'}>{t}</Tag>,
        },
        {
            title: 'Status', dataIndex: 'status', width: 110,
            render: (s) => <Tag color={s === 'success' ? 'green' : 'red'}>{s}</Tag>,
        },
        { title: 'HTTP', dataIndex: 'httpStatus', width: 80, render: (v) => v || '—' },
        {
            title: 'By', dataIndex: 'triggeredBy', width: 140,
            render: (u) => (u ? (u.name || u.username || u.email) : 'system'),
        },
        { title: 'Message', dataIndex: 'message', ellipsis: true, render: (m) => m || '—' },
    ];

    return (
        <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
            {contextHolder}
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ marginBottom: 4 }}>Website Deployment</Title>
                    <Text type="secondary">
                        Control scheduled and on-demand rebuilds of auriksha.com. A rebuild bakes the
                        current gold rate into the static site so search engines always see today&apos;s number.
                    </Text>
                </div>

                {/* Status + manual deploy */}
                <Card size="small">
                    <Space wrap size="large" style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space direction="vertical" size={2}>
                            <Text type="secondary">Last deploy</Text>
                            <Space>
                                <Text strong>{fmtDate(cfg.lastRunAt)}</Text>
                                {cfg.lastStatus && (
                                    <Tag color={cfg.lastStatus === 'success' ? 'green' : 'red'}>{cfg.lastStatus}</Tag>
                                )}
                            </Space>
                        </Space>
                        <Space direction="vertical" size={2}>
                            <Text type="secondary"><ClockCircleOutlined /> Next scheduled</Text>
                            <Text strong>{cfg.enabled ? fmtDate(cfg.nextRunAt) : 'Schedule off'}</Text>
                        </Space>
                        <Popconfirm
                            title="Trigger a website deploy now?"
                            description="This rebuilds and redeploys auriksha.com on Render immediately."
                            okText="Deploy" cancelText="Cancel"
                            onConfirm={handleDeployNow}
                            disabled={!cfg.deployHookConfigured}
                        >
                            <Button type="primary" icon={<RocketOutlined />} loading={deploying}
                                disabled={!cfg.deployHookConfigured}>
                                Deploy Now
                            </Button>
                        </Popconfirm>
                    </Space>
                    {cfg.lastStatus === 'failed' && cfg.lastError && (
                        <Alert style={{ marginTop: 12 }} type="error" showIcon
                            message="Last deploy failed" description={cfg.lastError} />
                    )}
                </Card>

                {/* Deploy hook */}
                <Card title="Render Deploy Hook" size="small">
                    <Paragraph type="secondary" style={{ marginTop: 0 }}>
                        Paste the Deploy Hook URL from the website service on Render
                        (Settings → Deploy Hook). It is encrypted before storage and never shown again.
                    </Paragraph>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {cfg.deployHookConfigured ? (
                            <Text>Configured <Tag color="green">•••• {cfg.deployHookHint}</Tag></Text>
                        ) : (
                            <Tag color="orange">Not configured</Tag>
                        )}
                        <Input.Password
                            placeholder={cfg.deployHookConfigured
                                ? 'Leave blank to keep the current hook, or paste a new one to rotate'
                                : 'https://api.render.com/deploy/srv-xxxx?key=yyyy'}
                            value={hookInput}
                            onChange={(e) => setHookInput(e.target.value)}
                            autoComplete="off"
                        />
                        {cfg.deployHookConfigured && (
                            <Popconfirm title="Remove the stored deploy hook?" okText="Remove" cancelText="Cancel"
                                onConfirm={handleClearHook}>
                                <Button danger size="small">Remove hook</Button>
                            </Popconfirm>
                        )}
                    </Space>
                </Card>

                {/* Schedule */}
                <Card title="Schedule" size="small"
                    extra={<Switch checked={cfg.enabled} onChange={(v) => patch({ enabled: v })}
                        checkedChildren="On" unCheckedChildren="Off" />}>
                    <Space direction="vertical" size="middle" style={{ width: '100%', opacity: cfg.enabled ? 1 : 0.6 }}>
                        <div>
                            <Text strong>Frequency</Text><br />
                            <Radio.Group value={cfg.frequency} disabled={!cfg.enabled}
                                onChange={(e) => patch({ frequency: e.target.value })} style={{ marginTop: 8 }}>
                                <Radio.Button value="daily">Daily</Radio.Button>
                                <Radio.Button value="weekly">Weekly / custom days</Radio.Button>
                            </Radio.Group>
                        </div>

                        {cfg.frequency === 'weekly' && (
                            <div>
                                <Text strong>Days</Text><br />
                                <Checkbox.Group options={DAY_OPTIONS} value={cfg.daysOfWeek} disabled={!cfg.enabled}
                                    onChange={(v) => patch({ daysOfWeek: v })} style={{ marginTop: 8 }} />
                            </div>
                        )}

                        <Space size="large" wrap>
                            <div>
                                <Text strong>Time</Text><br />
                                <Input type="time" value={cfg.time} disabled={!cfg.enabled}
                                    onChange={(e) => patch({ time: e.target.value })}
                                    style={{ width: 140, marginTop: 8 }} />
                            </div>
                            <div>
                                <Text strong>Timezone</Text><br />
                                <Select options={TZ_OPTIONS} value={cfg.timezone} disabled={!cfg.enabled}
                                    onChange={(v) => patch({ timezone: v })}
                                    style={{ width: 200, marginTop: 8 }} showSearch />
                            </div>
                        </Space>
                    </Space>

                    <Divider />

                    <Space align="start">
                        <Switch checked={cfg.autoDeployAfterRateUpdate}
                            onChange={(v) => patch({ autoDeployAfterRateUpdate: v })} />
                        <div>
                            <Text strong>Auto-deploy after each gold-rate update</Text>
                            <br />
                            <Text type="secondary">
                                Rebuild the website automatically the moment the day&apos;s gold rate advances,
                                so the site is never out of sync. Works independently of the schedule above.
                            </Text>
                            {cfg.lastDeployedRateDate && (
                                <div><Text type="secondary">Last rate deployed for: {cfg.lastDeployedRateDate}</Text></div>
                            )}
                        </div>
                    </Space>
                </Card>

                <Space>
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                        Save Settings
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={loadAll}>Refresh</Button>
                </Space>

                {/* History */}
                <Card title="Recent Deploys" size="small">
                    <Table rowKey={(r) => r._id} columns={logColumns} dataSource={logs}
                        size="small" pagination={{ pageSize: 10, hideOnSinglePage: true }}
                        locale={{ emptyText: 'No deploys yet' }} scroll={{ x: 720 }} />
                </Card>
            </Space>
        </div>
    );
}
